import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IXP_TEMPLATES, LOCATIONS, PROVIDER_TEMPLATES } from '@hosting-sim/core';
import { Button, Card, Input, Modal, Notice, Select } from '../components/ui';
import { ChatPanel } from '../components/ChatPanel';
import { MembersPanel } from '../components/GameMembers';
import { api, type GameListEntry } from '../lib/api';
import { useAuth, useTheme } from '../store/auth';
import { BrandLogo } from '../components/layout/BrandLogo';
import { SiteFooter } from '../components/layout/SiteFooter';
import { COUNTRY_NAMES, gameDay, money, num, sinceDate } from '../lib/format';
import { DEFAULT_APPEARANCE, saveAppearance, type Appearance } from '../lib/appearance';
import { AppearanceEditor } from '../components/figur/AppearanceEditor';

/**
 * Übersicht der Partien – dargestellt als bestücktes Rack.
 *
 * Jede Partie ist ein Einschub: Blende mit Lüftungsgitter, Statusleuchten,
 * Beschriftung und einem Display, das Spieldatum, Kasse und Kundenzahl zeigt.
 * Darunter bleibt eine freie Höheneinheit – das ist die Schaltfläche für eine
 * neue Partie. Wer das Spiel öffnet, steht damit vor seinem eigenen Rack, bevor
 * er die erste Zeile Verwaltung sieht.
 *
 * Vier Entscheidungen, die man kennen sollte:
 *
 *  - Das Rack ist in beiden Farbschemata dunkel und benutzt keine `dark:`-Regeln.
 *    Es stellt ein Gerät dar, kein Panel; ein helles Rack gibt es nicht. Die
 *    Umschaltung wirkt weiterhin auf alles andere, auch auf die Dialoge dieser
 *    Seite.
 *  - Blenden, Schienen und Schrauben sind reines CSS (Verläufe, Innenschatten).
 *    Bilder wären schärfer, müssten aber für jede Höhe neu geschnitten werden
 *    und würden bei zwei Farbschemata doppelt anfallen.
 *  - Der Einschub rückt beim Überfahren zwei Pixel nach rechts, so als zöge man
 *    ihn aus dem Rack. Das ersetzt einen Schatten- oder Farbwechsel als
 *    Rückmeldung und bleibt in der Bildsprache.
 *  - Statt des Titelbilds trägt der freigestellte Schriftzug den Kopf. Das Foto
 *    stand hier vorher `object-contain` in einem Kasten und bekam auf breiten
 *    Schirmen schwarze Balken; hinter einem Rack wäre es ohnehin zu unruhig.
 */

const DIFFICULTIES = [
  { value: 'easy', label: 'Einsteiger', hint: '150.000 € Startkapital, mehr Nachfrage, seltenere Störungen.' },
  { value: 'normal', label: 'Normal', hint: '80.000 € Startkapital, ausgeglichene Bedingungen.' },
  { value: 'hard', label: 'Schwer', hint: '45.000 € Startkapital, häufige Störungen, höhere Kosten.' },
];

/**
 * Der Umfang des Standortkatalogs, wie auf der Anmeldeseite aus den Daten
 * gezählt. Er steht unten im Rack auf einer Blende – wer den Katalog erweitert,
 * ändert damit auch diese Zeile.
 */
const CATALOG_FACTS = [
  { value: LOCATIONS.length, label: 'Standorte' },
  { value: IXP_TEMPLATES.length, label: 'Internetknoten' },
  { value: PROVIDER_TEMPLATES.length, label: 'Netze' },
];

/**
 * Montageschiene: dunkles Metall mit einer Lochreihe.
 *
 * Die Löcher liegen in einer eigenen Ebene, die nur senkrecht kachelt und
 * waagerecht mittig sitzt – so bleiben sie 8 px breit, egal wie breit die
 * Schiene ist.
 */
const RAIL: CSSProperties = {
  backgroundColor: '#161d26',
  backgroundImage:
    'repeating-linear-gradient(to bottom, rgba(0,0,0,0.85) 0 5px, rgba(255,255,255,0.06) 5px 6px, transparent 6px 20px),' +
    'linear-gradient(to right, rgba(255,255,255,0.10), rgba(0,0,0,0.30) 60%, rgba(0,0,0,0.55))',
  backgroundSize: '8px 20px, 100% 100%',
  backgroundRepeat: 'repeat-y, no-repeat',
  backgroundPosition: 'center top, 0 0',
};

/** Frontblende eines Einschubs: gebürstetes Metall mit Lichtkante oben. */
const CHASSIS: CSSProperties = {
  backgroundImage: 'linear-gradient(180deg, #2c3641 0%, #212a34 45%, #1a212a 100%)',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.09), inset 0 -1px 0 rgba(0,0,0,0.5), 0 2px 4px rgba(0,0,0,0.45)',
};

/** Lüftungsgitter – ein Punktraster, kein Bild. */
const GRILLE: CSSProperties = {
  backgroundImage: 'radial-gradient(circle, rgba(0,0,0,0.62) 1.1px, transparent 1.4px)',
  backgroundSize: '5px 5px',
  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05), inset 0 1px 3px rgba(0,0,0,0.6)',
};

/** Display: schwarzes Glas, tief eingesetzt. */
const DISPLAY: CSSProperties = {
  backgroundColor: '#04080f',
  boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.95), inset 0 0 0 1px rgba(255,255,255,0.05)',
};

/** Leuchtender Ziffernsatz auf dem Display. */
const DISPLAY_TEXT: CSSProperties = { textShadow: '0 0 10px rgba(69,190,139,0.45)' };

/** Schaltfläche auf dunklem Grund – dieselbe Form wie auf der Anmeldeseite. */
const GLASS_BUTTON =
  'rounded-md border border-white/15 bg-white/10 px-2.5 py-1.5 text-sm text-white/80 backdrop-blur transition-colors hover:bg-white/20 hover:text-white';

export function GameList() {
  const [games, setGames] = useState<GameListEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<GameListEntry | null>(null);
  const [pendingMembers, setPendingMembers] = useState<GameListEntry | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const { dark, toggle } = useTheme();

  const reload = useCallback(async () => {
    try {
      setGames(await api.listGames());
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Zuletzt gespielt zuoberst. Die Reihenfolge des Servers ist nicht zugesichert,
  // und wer sich anmeldet, will fast immer da weitermachen, wo er aufgehört hat.
  const sorted = useMemo(
    () => [...(games ?? [])].sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt)),
    [games],
  );

  const summary = useMemo(() => {
    if (!sorted.length) return null;
    const running = sorted.filter((g) => g.running).length;
    const parts = [`${sorted.length} ${sorted.length === 1 ? 'Partie' : 'Partien'}`];
    if (running > 0) parts.push(`${running} ${running === 1 ? 'läuft' : 'laufen'}`);
    parts.push(`zuletzt gespielt ${sinceDate(sorted[0].updatedAt)}`);
    return parts.join(' · ');
  }, [sorted]);

  const firstName = user?.displayName?.trim().split(/\s+/)[0];

  return (
    // Zur Flex-Spalte auf demselben Element wie `min-h-full` siehe die
    // Anmerkung auf der Anmeldeseite: geschachtelt löst sich die Höhe zu null auf.
    <div className="relative isolate flex min-h-full flex-col overflow-hidden bg-[#070b12]">
      {/* --- Raum ----------------------------------------------------------- */}
      {/* Kaltgang statt Titelbild: ein blauer Schein hinter dem Schriftzug, ein
          grüner Rest am Boden. Beide sehr schwach – der Grund soll Tiefe geben
          und nicht mit den Geräten um Aufmerksamkeit streiten. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0"
        style={{
          background:
            'radial-gradient(52rem 26rem at 50% -6rem, rgba(56,120,220,0.20), transparent 70%),' +
            'radial-gradient(60rem 30rem at 50% 118%, rgba(20,133,91,0.12), transparent 68%)',
        }}
      />

      <div className="relative z-10 flex flex-1 flex-col">
        <header className="mx-auto w-full max-w-5xl px-4 pt-4 sm:px-6 sm:pt-6">
          <div className="flex justify-end gap-2">
            <button type="button" onClick={toggle} aria-label="Farbschema wechseln" className={GLASS_BUTTON}>
              {dark ? '☀' : '☾'}
            </button>
            <button type="button" className={GLASS_BUTTON} onClick={() => setChatOpen((v) => !v)}>
              Chat
            </button>
            <Link to="/leaderboard" className={GLASS_BUTTON}>
              Bestenliste
            </Link>
            {user?.platformRole !== 'USER' && (
              <button type="button" className={GLASS_BUTTON} onClick={() => navigate('/admin')}>
                Verwaltung
              </button>
            )}
            <button
              type="button"
              className={GLASS_BUTTON}
              onClick={async () => {
                await logout();
                navigate('/login');
              }}
            >
              Abmelden
            </button>
          </div>

          <BrandLogo priority className="mx-auto mt-1 w-[15rem] sm:mt-2 sm:w-[19rem]" />

          <div className="mt-2 flex flex-wrap items-end justify-between gap-3 sm:mt-4">
            <div className="min-w-0">
              <h1 className="text-xl font-semibold tracking-tight text-white sm:text-2xl">
                {firstName ? `Willkommen zurück, ${firstName}.` : 'Willkommen zurück.'}
              </h1>
              <p className="mt-1.5 font-mono text-2xs uppercase tracking-[0.16em] text-white/40">
                {summary ?? 'Rack leer – noch kein Einschub bestückt'}
              </p>
            </div>
            <Button variant="primary" onClick={() => setCreating(true)}>
              Neue Partie
            </Button>
          </div>
        </header>

        <main className="mx-auto w-full max-w-5xl flex-1 px-4 pb-12 pt-5 sm:px-6 sm:pt-6">
          {error && (
            <div className="mb-4">
              <Notice tone="danger" title="Laden fehlgeschlagen">
                {error}
              </Notice>
            </div>
          )}

          {chatOpen && (
            <div className="mb-4">
              <Card title="Lobby-Chat" subtitle="Sichtbar für alle angemeldeten Spieler.">
                <ChatPanel channel="lobby" compact />
              </Card>
            </div>
          )}

          <Rack>
            {/* Beim Laden zwei Blenden in der Höhe des späteren Einschubs: Die
                Seite springt beim Eintreffen der Daten nicht. */}
            {games === null &&
              [0, 1].map((i) => (
                <div key={i} className="h-[4.75rem] animate-pulse-soft rounded-sm motion-reduce:animate-none" style={CHASSIS} />
              ))}

            {sorted.map((game) => (
              <GameUnit key={game.id} game={game} onDelete={() => setPendingDelete(game)} onManageMembers={() => setPendingMembers(game)} />
            ))}

            {games !== null && <FreeSlot first={sorted.length === 0} onClick={() => setCreating(true)} />}

            {/* Blindblenden füllen das Rack bis zu einer Mindesthöhe auf. Ein
                echtes Rack steht nie halb offen da, und die Seite fiele bei
                einer einzigen Partie sonst auf eine schmale Leiste zusammen. */}
            {games !== null &&
              Array.from({ length: Math.max(0, 4 - sorted.length) }, (_, i) => <BlankPanel key={i} />)}

            <CatalogPanel />
          </Rack>
        </main>

        <SiteFooter />
      </div>

      <NewGameDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id) => {
          setCreating(false);
          navigate(`/game/${id}`);
        }}
      />

      <DeleteDialog
        game={pendingDelete}
        onClose={() => setPendingDelete(null)}
        onDeleted={() => {
          setPendingDelete(null);
          void reload();
        }}
      />

      <MembersDialog game={pendingMembers} onClose={() => setPendingMembers(null)} />
    </div>
  );
}

/**
 * Der Rahmen: zwei Montageschienen, dazwischen die Einschübe.
 *
 * Die Schienen liegen als eigene Ebene über die volle Höhe, damit sie hinter
 * den Lücken zwischen den Einschüben durchlaufen – genau das macht aus einer
 * Liste von Kacheln ein Rack.
 */
function Rack({ children }: { children: React.ReactNode }) {
  return (
    <section
      className="relative rounded-lg p-1.5 ring-1 ring-white/[0.07]"
      style={{
        backgroundColor: '#0b1119',
        boxShadow: '0 40px 90px -30px rgba(0,0,0,0.95), inset 0 1px 0 rgba(255,255,255,0.05)',
      }}
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-1.5 left-1.5 w-4 rounded-sm" style={RAIL} />
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-1.5 right-1.5 w-4 rounded-sm" style={RAIL} />
      <div className="flex flex-col gap-1.5 px-[1.375rem]">{children}</div>

      {/* Schlagschatten auf den Boden. Ohne ihn schwebt das Rack im Nichts –
          gerade bei wenigen Einschüben, wenn viel leere Fläche darunter liegt. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-8 top-full h-12"
        style={{ background: 'radial-gradient(50% 100% at 50% 0%, rgba(0,0,0,0.65), transparent 72%)' }}
      />
    </section>
  );
}

/** Schraube in der Montageöse eines Einschubs. */
function Screw() {
  return (
    <span
      aria-hidden="true"
      className="block h-1.5 w-1.5 rounded-full"
      style={{
        background: 'radial-gradient(circle at 35% 30%, #c2ccd8, #59636f 62%, #2b323b)',
        boxShadow: '0 1px 1px rgba(0,0,0,0.7)',
      }}
    />
  );
}

/** Statusleuchte. Grün heißt in Betrieb, gedämpft heißt angehalten. */
function Led({ tone, pulse }: { tone: 'power' | 'link' | 'idle'; pulse?: boolean }) {
  const color = { power: '#45be8b', link: '#5aa7f0', idle: '#3d4753' }[tone];
  return (
    <span
      aria-hidden="true"
      className={`block h-1.5 w-1.5 rounded-full ${pulse ? 'animate-pulse-soft motion-reduce:animate-none' : ''}`}
      style={{ backgroundColor: color, boxShadow: tone === 'idle' ? 'inset 0 0 2px rgba(0,0,0,0.8)' : `0 0 6px ${color}` }}
    />
  );
}

/**
 * Eine Partie als Einschub.
 *
 * Die ganze Blende ist anklickbar: Der Verweis auf "Öffnen" legt sich über
 * `after:` unsichtbar über die Fläche. Das Löschen liegt mit `z-10` darüber,
 * sonst finge der Verweis den Klick ab. Ein Verweis um die ganze Blende wäre die
 * naheliegende Alternative, dürfte aber keine Schaltfläche enthalten.
 */
function GameUnit({ game, onDelete, onManageMembers }: { game: GameListEntry; onDelete: () => void; onManageMembers: () => void }) {
  // Der Autosave ist der übliche Fall. Fehlt er – etwa weil nur von Hand
  // gespeichert wurde –, bliebe das Display sonst leer; dann zählt der weiteste
  // Spielstand.
  const save = game.saves.find((s) => s.slot === 0) ?? [...game.saves].sort((a, b) => b.tick - a.tick)[0];
  const difficulty = DIFFICULTIES.find((d) => d.value === game.difficulty)?.label ?? game.difficulty;

  return (
    <article
      className="group relative flex flex-wrap items-center gap-x-3 gap-y-2 rounded-sm px-2.5 py-2.5 transition-transform duration-200 hover:translate-x-0.5 focus-within:translate-x-0.5"
      style={CHASSIS}
    >
      {/* Montageösen links und rechts. Auf schmalen Schirmen zählt jeder
          Millimeter Breite – dort entfallen sie. */}
      <div className="hidden flex-col justify-between self-stretch py-0.5 sm:flex">
        <Screw />
        <Screw />
      </div>

      <div aria-hidden="true" className="hidden h-9 w-8 shrink-0 rounded-[3px] sm:block" style={GRILLE} />

      <div className="flex shrink-0 flex-col gap-1 self-center pl-0.5">
        <Led tone={game.running ? 'power' : 'idle'} pulse={game.running} />
        <Led tone={game.running ? 'link' : 'idle'} />
      </div>

      {/* Beschriftung der Blende: Name groß, darunter die Angaben, die man zum
          Wiedererkennen braucht – Firma, Schwierigkeit, Stand der Speicherung. */}
      <div className="min-w-0 flex-1">
        <h2 className="truncate text-sm font-semibold text-white">{game.name}</h2>
        <p className="truncate font-mono text-2xs uppercase tracking-[0.14em] text-white/40">
          {game.companyName} · {difficulty} · {game.saves.length} {game.saves.length === 1 ? 'Spielstand' : 'Spielstände'}
        </p>
        <p className="mt-0.5 truncate text-2xs text-white/25">Gespeichert {sinceDate(game.updatedAt)}</p>
      </div>

      {/* Display. Auf schmalen Schirmen rutscht es unter die Beschriftung und
          nimmt die volle Breite – deshalb `order-last` und `w-full`. */}
      <div className="order-last w-full rounded-[4px] px-2 py-1.5 md:order-none md:w-auto md:px-3" style={DISPLAY}>
        <div className="grid grid-cols-3 gap-x-2 text-center md:gap-x-3">
          {[
            { label: 'Spieldatum', value: save ? gameDay(save.gameDate) : '––' },
            { label: 'Kasse', value: save ? money(save.cash, true) : '––' },
            { label: 'Kunden', value: save ? num(save.customers) : '––' },
          ].map((cell) => (
            <div key={cell.label} className="md:min-w-[5.5rem]">
              <p className="tabular truncate font-mono text-xs font-semibold leading-none text-accent-300" style={DISPLAY_TEXT}>
                {cell.value}
              </p>
              <p className="mt-1 font-mono text-2xs uppercase tracking-[0.12em] text-white/30">{cell.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Auf Telefonen bekommen die Schaltflächen eine eigene Zeile: Neben dem
          Namen bliebe von diesem sonst nur ein Wortanfang übrig. */}
      <div className="order-last flex w-full shrink-0 items-center justify-end gap-1.5 sm:order-none sm:w-auto">
        <button
          type="button"
          onClick={onManageMembers}
          aria-label={`Mitglieder von ${game.name} verwalten`}
          className="relative z-10 rounded-md px-2 py-1 text-xs text-white/40 transition-colors hover:bg-white/10 hover:text-white/80"
        >
          Mitspieler
        </button>
        {game.isOwner && (
          <button
            type="button"
            onClick={onDelete}
            aria-label={`Partie ${game.name} löschen`}
            className="relative z-10 rounded-md px-2 py-1 text-xs text-white/40 transition-colors hover:bg-red-500/15 hover:text-red-300"
          >
            Löschen
          </button>
        )}
        <Link
          to={`/game/${game.id}`}
          className="inline-flex items-center gap-1.5 rounded-md bg-accent-600 px-3 py-1.5 text-sm font-medium text-white transition-colors after:absolute after:inset-0 after:content-[''] hover:bg-accent-700"
        >
          Öffnen
          <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">
            →
          </span>
        </Link>
      </div>
    </article>
  );
}

/**
 * Die freie Höheneinheit unter den bestückten Einschüben.
 *
 * Sie ersetzt den früheren Leerzustand: Ein leeres Rack ist keine leere Seite,
 * sondern eines mit einem freien Platz – und der freie Platz ist genau die
 * Einladung, eine Partie anzulegen.
 */
function FreeSlot({ first, onClick }: { first: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full flex-col items-center justify-center gap-1 rounded-sm border border-dashed border-white/15 bg-black/30 px-4 py-5 text-center transition-colors hover:border-accent-400/60 hover:bg-accent-500/[0.07]"
    >
      <span className="font-mono text-2xs uppercase tracking-[0.2em] text-white/45 transition-colors group-hover:text-accent-300">
        + Freier Einschub · Neue Partie
      </span>
      {first && (
        <span className="max-w-md text-xs text-white/40">
          Starte als kleiner Anbieter mit einem halben Rack, einem Router und drei Servern – und baue daraus ein eigenes Netz.
        </span>
      )}
    </button>
  );
}

/** Blindblende: geschlossene Höheneinheit ohne Gerät dahinter. */
function BlankPanel() {
  return (
    <div aria-hidden="true" className="flex h-7 items-center gap-3 rounded-sm px-2.5" style={CHASSIS}>
      <Screw />
      <div className="h-2 flex-1 rounded-[2px]" style={GRILLE} />
      <Screw />
    </div>
  );
}

/**
 * Unterste Blende: der Umfang der Simulation in drei Zahlen.
 *
 * Sie steht auf der Anmeldeseite schon am Fuß der Anmeldemaske. Hier schließt
 * sie das Rack nach unten ab, damit es nicht mit einem gestrichelten Rahmen
 * endet – und sie sagt mehr über das Spiel als ein Werbesatz.
 */
function CatalogPanel() {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 rounded-sm px-3 py-2" style={CHASSIS}>
      <span className="flex items-center gap-2 font-mono text-2xs uppercase tracking-[0.2em] text-white/35">
        <Led tone="link" />
        Katalog
      </span>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
        {CATALOG_FACTS.map((fact) => (
          <span key={fact.label} className="flex items-baseline gap-1.5">
            <span className="tabular font-mono text-xs font-semibold text-accent-300" style={DISPLAY_TEXT}>
              {fact.value}
            </span>
            <span className="font-mono text-2xs uppercase tracking-[0.14em] text-white/30">{fact.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Rückfrage vor dem Löschen.
 *
 * Statt `confirm()`: Der Systemdialog nennt nur den Namen, nicht den Umfang des
 * Verlusts, und er fällt aus der Gestaltung der Seite. Hier steht der Spielstand
 * mit Datum und Kasse daneben – das ist die Angabe, die man für die
 * Entscheidung braucht.
 */
function DeleteDialog({ game, onClose, onDeleted }: { game: GameListEntry | null; onClose: () => void; onDeleted: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = game?.saves.find((s) => s.slot === 0) ?? game?.saves[0];

  const remove = async () => {
    if (!game) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteGame(game.id);
      onDeleted();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={game !== null}
      onClose={onClose}
      title="Partie löschen"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="danger" loading={busy} onClick={remove}>
            Endgültig löschen
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p>
          Soll <strong>{game?.name}</strong> mit allen Spielständen gelöscht werden? Das lässt sich nicht rückgängig machen.
        </p>
        {save && (
          <div className="surface-muted p-3 text-xs text-panel-600 dark:text-panel-300">
            Stand vom {gameDay(save.gameDate)} · {money(save.cash)} · {num(save.customers)} Kunden
          </div>
        )}
        {error && <Notice tone="danger">{error}</Notice>}
      </div>
    </Modal>
  );
}

/** Dialograhmen um {@link MembersPanel} für die Partienübersicht. */
function MembersDialog({ game, onClose }: { game: GameListEntry | null; onClose: () => void }) {
  if (!game) return null;
  return (
    <Modal open onClose={onClose} title={`Mitspieler – ${game.name}`}>
      <MembersPanel gameId={game.id} />
    </Modal>
  );
}

function NewGameDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState('Erste Partie');
  const [companyName, setCompanyName] = useState('NordHost');
  const [difficulty, setDifficulty] = useState('normal');
  const [location, setLocation] = useState('loc-fra');
  const [seed, setSeed] = useState('');
  const [appearance, setAppearance] = useState<Appearance>(DEFAULT_APPEARANCE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startLocations = LOCATIONS.filter((l) => l.startingLocation);
  const selected = DIFFICULTIES.find((d) => d.value === difficulty);
  const chosenLocation = startLocations.find((l) => l.key === location);

  // Deutschland zuerst, danach die Nachbarländer alphabetisch – der Heimatmarkt
  // ist der übliche Einstieg und soll nicht gesucht werden müssen.
  const startsByCountry = useMemo(() => {
    const groups = new Map<string, typeof startLocations>();
    for (const l of startLocations) {
      const list = groups.get(l.country) ?? [];
      list.push(l);
      groups.set(l.country, list);
    }
    for (const list of groups.values()) list.sort((a, b) => a.powerPricePerKwh - b.powerPricePerKwh);
    return [...groups.entries()].sort(([a], [b]) => (a === 'DE' ? -1 : b === 'DE' ? 1 : a.localeCompare(b)));
  }, [startLocations]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.createGame({
        name,
        companyName,
        difficulty,
        startLocation: location,
        seed: seed.trim() ? Number(seed) : undefined,
      });
      saveAppearance(result.gameId, appearance);
      onCreated(result.gameId);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Neue Partie anlegen"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" loading={busy} onClick={submit}>
            Partie starten
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input label="Name der Partie" value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="Firmenname" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />

        <Select label="Schwierigkeit" value={difficulty} onChange={(e) => setDifficulty(e.target.value)} hint={selected?.hint}>
          {DIFFICULTIES.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </Select>

        <Select
          label="Erster Standort"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          hint={
            chosenLocation
              ? `${chosenLocation.description} · ${chosenLocation.monthlyRent.toLocaleString('de-DE')} €/Monat Miete · Kundenpotenzial ${chosenLocation.marketPotential.toFixed(2)}`
              : undefined
          }
        >
          {/* Nach Land gruppiert: Die Liste ist zu lang geworden, um sie am
              Stück zu überblicken. Innerhalb eines Landes entscheidet der
              Strompreis über die Reihenfolge – das ist die Zahl, wegen der man
              überhaupt vom Standardstandort abweicht. */}
          {startsByCountry.map(([country, orte]) => (
            <optgroup key={country} label={COUNTRY_NAMES[country] ?? country}>
              {orte.map((l) => (
                <option key={l.key} value={l.key}>
                  {l.city} – {l.powerPricePerKwh.toFixed(2)} €/kWh, Potenzial {l.marketPotential.toFixed(2)}
                </option>
              ))}
            </optgroup>
          ))}
        </Select>

        <Input
          label="Startwert (optional)"
          value={seed}
          onChange={(e) => setSeed(e.target.value.replace(/\D/g, ''))}
          placeholder="z. B. 4242"
          hint="Gleicher Startwert erzeugt denselben Spielverlauf – praktisch zum Vergleichen von Strategien."
        />

        {error && <Notice tone="danger">{error}</Notice>}
        <div>
          <div className="mb-2 text-sm font-medium">Deine Figur</div>
          <AppearanceEditor value={appearance} onChange={setAppearance} />
        </div>
      </div>
    </Modal>
  );
}
